import ArchiveConfirmationDialog from "@src/features/contentLibrary/dialogs/ArchiveConfirmationDialog";
import { DropdownMenuItem } from "@src/lib/ui/DropdownMenu";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import { useToast } from "@src/lib/hooks/useToast";
import { FC, ReactElement, useState } from "react";
import UTurnToLeftOutlineIcon from "@src/lib/icons/18px/u-turn-to-left-outline";
import PenOutlineIcon from "@src/lib/icons/18px/pen-outline";
import BookOpenOutlineIcon from "@src/lib/icons/18px/book-open-outline";
import SquaresCopyOutlineIcon from "@src/lib/icons/18px/squares-copy-outline";
import TrashOutlineIcon from "@src/lib/icons/18px/trash-outline";
import TranslateTextDialog from "../app/dialogs/translationSetDialogs/TranslateTextDialog";
import useContentPermissions from "@src/features/libraryItem/hooks/useContentPermissions";
import { CONTENT_LIBRARY_REFETCH_QUERIES } from "@src/lib/contexts/TagContext/TagContext";
import CopyModuleDialog from "../libraryItem/module/build/CopyModuleDialog";
import LibraryAccessAutomationDialog from "./assignmentDialogs/LibraryAccessAutomationDialog";
import { useLibraryItemAccessAutomationQuery } from "@src/features/library/LibraryPaginatedTable.generated";
import { useArchiveModuleMutation } from "@src/features/libraryItem/LibraryItemDetailPageContainer.generated";
import { ModuleLibraryPaginatedTable_LibraryItemFragment } from "../library/modules/ModulesLibraryPaginatedTable.generated";

type Props = {
  libraryItem: ModuleLibraryPaginatedTable_LibraryItemFragment;
  className?: string;
  iconClassName?: string;
  rotateIcon?: boolean;
  isVisible: boolean;
};

export function ModuleOverflowMenuItems({
  libraryItem,
  onRename,
  onArchive,
  onDuplicate,
  onUpdateAccess,
}: {
  libraryItem: ModuleLibraryPaginatedTable_LibraryItemFragment;
  onRename: () => void;
  onArchive: () => void;
  onDuplicate: () => void;
  onUpdateAccess: () => void;
}): ReactElement | null {
  const module = libraryItem.module;
  const { userActionPermissions } = useActionPermissions();
  const permissions = useContentPermissions({ moduleId: module?.id });
  const { addToast } = useToast();
  const [archiveModule] = useArchiveModuleMutation({
    variables: {
      moduleId: module?.id ?? -1,
      archive: true,
    },
    refetchQueries: CONTENT_LIBRARY_REFETCH_QUERIES,
    onCompleted: (data) => {
      if (data.archiveModule.success) {
        addToast({
          message: "Module successfully unarchived",
        });
      }
    },
  });

  const { data: automationData } = useLibraryItemAccessAutomationQuery({
    variables: { id: libraryItem.id },
  });
  const automation = automationData?.LibraryItem?.accessAutomation;

  if (!permissions) return null;
  return (
    <>
      {libraryItem.archivedAt ? (
        <DropdownMenuItem
          disabled={!userActionPermissions?.manageLibrary}
          onSelect={() => {
            if (!module) return;
            archiveModule({
              variables: { moduleId: module.id, archive: false },
            });
          }}
        >
          <UTurnToLeftOutlineIcon className="size-4" />
          Unarchive
        </DropdownMenuItem>
      ) : (
        <>
          <DropdownMenuItem
            disabled={!permissions?.hasEditContentPermission}
            onSelect={onRename}
          >
            <PenOutlineIcon className="size-4" />
            Rename
          </DropdownMenuItem>
          {module?.publishedVersion && (
            <DropdownMenuItem
              disabled={
                !userActionPermissions?.updateAutomations || !automation
              }
              onSelect={onUpdateAccess}
            >
              <BookOpenOutlineIcon className="size-4" />
              Update Access
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            disabled={!userActionPermissions?.createContent}
            onSelect={() => {
              if (!module) return;
              onDuplicate();
            }}
          >
            <SquaresCopyOutlineIcon className="size-4" />
            Duplicate
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={!userActionPermissions?.manageLibrary}
            onSelect={onArchive}
          >
            <TrashOutlineIcon className="size-4" />
            Archive
          </DropdownMenuItem>
        </>
      )}
    </>
  );
}

export function ModuleOverflowDialogs({
  libraryItem,
  renameOpen,
  setRenameOpen,
  archiveOpen,
  setArchiveOpen,
  copyModuleDialogOpen,
  setCopyModuleDialogOpen,
  automationDialogOpen,
  setAutomationDialogOpen,
}: {
  libraryItem: ModuleLibraryPaginatedTable_LibraryItemFragment;
  renameOpen: boolean;
  setRenameOpen: (open: boolean) => void;
  archiveOpen: boolean;
  setArchiveOpen: (open: boolean) => void;
  copyModuleDialogOpen: boolean;
  setCopyModuleDialogOpen: (open: boolean) => void;
  automationDialogOpen: boolean;
  setAutomationDialogOpen: (open: boolean) => void;
}): ReactElement {
  const module = libraryItem.module;
  const { addToast, addErrorToast } = useToast();
  const [archiveModule] = useArchiveModuleMutation({
    variables: {
      moduleId: module?.id ?? -1,
      archive: true,
    },
    refetchQueries: CONTENT_LIBRARY_REFETCH_QUERIES,
    onCompleted: (data) => {
      if (data.archiveModule.success) {
        addToast({
          message: "Module successfully archived",
        });
      } else {
        addErrorToast({
          ...data.archiveModule,
          callsite: "archive_module_overflow",
        });
      }
    },
  });

  const { data: automationData } = useLibraryItemAccessAutomationQuery({
    variables: { id: libraryItem.id },
  });
  const automation = automationData?.LibraryItem?.accessAutomation;

  return (
    <>
      {renameOpen && (
        <TranslateTextDialog
          open={renameOpen}
          onOpenChange={setRenameOpen}
          label="Name"
          title="Edit Module Name"
          translationSetUuid={libraryItem.name.uuid!}
        />
      )}
      {copyModuleDialogOpen && module && (
        <CopyModuleDialog
          moduleId={module.id}
          open={copyModuleDialogOpen}
          onOpenChange={setCopyModuleDialogOpen}
        />
      )}
      {archiveOpen && (
        <ArchiveConfirmationDialog
          open={archiveOpen}
          onOpenChange={setArchiveOpen}
          onArchive={archiveModule}
          title={`Archive Module`}
          itemName={libraryItem.name.en}
        />
      )}
      {automationDialogOpen && automation && (
        <LibraryAccessAutomationDialog
          open={automationDialogOpen}
          onOpenChange={setAutomationDialogOpen}
          automationItem={{
            automation,
            contentName: libraryItem.name.en,
            __typename: "Module" as const,
          }}
          refetchQueries={CONTENT_LIBRARY_REFETCH_QUERIES}
        />
      )}
    </>
  );
}

const ModuleOverflowMenu: FC<Props> = ({ libraryItem }) => {
  const [copyModuleDialogOpen, setCopyModuleDialogOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [automationDialogOpen, setAutomationDialogOpen] = useState(false);

  return (
    <>
      <ModuleOverflowMenuItems
        libraryItem={libraryItem}
        onRename={() => setRenameOpen(true)}
        onArchive={() => setArchiveOpen(true)}
        onDuplicate={() => setCopyModuleDialogOpen(true)}
        onUpdateAccess={() => setAutomationDialogOpen(true)}
      />
      <ModuleOverflowDialogs
        libraryItem={libraryItem}
        renameOpen={renameOpen}
        setRenameOpen={setRenameOpen}
        archiveOpen={archiveOpen}
        setArchiveOpen={setArchiveOpen}
        copyModuleDialogOpen={copyModuleDialogOpen}
        setCopyModuleDialogOpen={setCopyModuleDialogOpen}
        automationDialogOpen={automationDialogOpen}
        setAutomationDialogOpen={setAutomationDialogOpen}
      />
    </>
  );
};

export default ModuleOverflowMenu;
